import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../../api/config";
import EmpLayout from "./emp_layout";
import './css/post_job.css';

export default function PostJob() {
  const navigate = useNavigate();
  const dropdownRef = useRef(null); 

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [skillInput, setSkillInput] = useState("");
  
  // States matching your API payloads
  const [countiesList, setCountiesList] = useState([]);
  const [globalSkillsPool, setGlobalSkillsPool] = useState([]); // Master database list
  const [globalSpecialisations, setGlobalSpecialisations] = useState([]);
  const [showSkillDropdown, setShowSkillDropdown] = useState(false);

  // Fetch dimensions on mount
  useEffect(() => {
    const fetchDimensions = async () => {
      const token = localStorage.getItem("token");
      const headers = { "Authorization": `Bearer ${token}` };

      try {
        const [countyRes, skillsRes, specialisationsRes] = await Promise.all([
          fetch(`${API_ENDPOINTS.DIMENSIONS}/counties`, { headers }),
          fetch(`${API_ENDPOINTS.DIMENSIONS}/skills-pool`, { headers }),
          fetch(`${API_ENDPOINTS.DIMENSIONS}/specialisations`, { headers })
        ]);

        if (countyRes.ok) setCountiesList(await countyRes.json());
        
        // FIX 1: Save data straight into the correct state array used for filtering below
        if (skillsRes.ok) setGlobalSkillsPool(await skillsRes.json());
        if (specialisationsRes.ok) setGlobalSpecialisations(await specialisationsRes.json());

      } catch (err) {
        console.error("Error fetching form dimensions:", err);
      }
    };

    fetchDimensions();
  }, []);

  // Click outside dropdown logic
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSkillDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [form, setForm] = useState({
    title: "", description: "", type: "Full-time", spec: "", city: "",
    salaryMin: "", salaryMax: "", salaryCurrency: "KSh", salaryPer: "month",
    experience: "", deadline: "", skills: [],
  });

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  // Skills Handling Logic
  const handleSelectSkill = (skillName) => {
    if (skillName && !form.skills.includes(skillName)) {
      setForm(f => ({ ...f, skills: [...f.skills, skillName] }));
    }
    setSkillInput("");
    setShowSkillDropdown(false);
  };

  const removeSkill = (s) => setForm(f => ({ ...f, skills: f.skills.filter(x => x !== s) }));

  // FIX 2: Filter from globalSkillsPool (Check if it's an array of strings or objects)
  const filteredSkillsPool = globalSkillsPool.filter(pooledSkill => {
    // If your API returns an array of objects like [{id: 1, name: "Balayage"}], change pooledSkill to pooledSkill.name
    const nameString = typeof pooledSkill === 'string' ? pooledSkill : pooledSkill.name || "";
    
    return nameString.toLowerCase().includes(skillInput.toLowerCase()) &&
           !form.skills.includes(nameString);
  });

  const handleSubmit = async () => {
    if (!form.title || !form.description || !form.spec || !form.city) return;

    setLoading(true);
    const token = localStorage.getItem("token");

    const payload = {
      Title: form.title,
      Description: form.description,
      SpecialisationId: parseInt(form.spec), // Matches C# DTO int ID requirement
      JobType: form.type,
      City: form.city,
      SalaryMin: form.salaryMin ? parseFloat(form.salaryMin) : null,
      SalaryMax: form.salaryMax ? parseFloat(form.salaryMax) : null,
      SalaryCurrency: form.salaryCurrency,
      SalaryPer: form.salaryPer,
      ExperienceRequired: form.experience,
      ApplicationDeadline: form.deadline ? form.deadline : null,
      Skills: form.skills
    };

    try {
      const response = await fetch(API_ENDPOINTS.POSTJOB, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSubmitted(true);
      }
    } catch (error) {
      console.error("Post failed:", error);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) return (
    <EmpLayout title="Post a" titleEm="Job">
      <div className="pj-success">
        <div className="pj-success-icon">✅</div>
        <div className="pj-success-title">Job posted!</div>
        <div className="pj-success-sub">
          <strong>{form.title}</strong> is now live and visible to thousands of beauty professionals on BeautyLink.<br />
          Candidates can start applying immediately.
        </div>
        <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
          <button className="emp-btn-primary" onClick={() => navigate("/employer/listings")}>View My Listings</button>
          <button className="emp-btn-ghost" onClick={() => { setSubmitted(false); setForm({ title: "", description: "", type: "Full-time", spec: "", city: "", salaryMin: "", salaryMax: "", salaryCurrency: "KSh", salaryPer: "month", experience: "", deadline: "", skills: [] }); }}>Post Another</button>
        </div>
      </div>
    </EmpLayout>
  );

  const salaryPreview = form.salaryMin ? `${form.salaryCurrency} ${Number(form.salaryMin).toLocaleString()}${form.salaryMax ? ` – ${Number(form.salaryMax).toLocaleString()}` : ""}/${form.salaryPer === "month" ? "mo" : "day"}` : "Not specified";

  // Find current specialisation name string for the live preview side panel
  const selectedSpecObj = globalSpecialisations.find(s => String(s.id) === String(form.spec));
  const previewSpecName = selectedSpecObj ? selectedSpecObj.name : "";

  return (
    <EmpLayout title="Post a" titleEm="Job">
      <div className="pj-layout">
        {/* Form Container */}
        <div>
          {/* Basic Info Card */}
          <div className="pj-card">
            <div className="pj-card-title">Basic <em>information</em></div>
            <div className="emp-field">
              <label className="emp-label">Job Title *</label>
              <input className="emp-input" placeholder="e.g. Senior Hair Stylist" value={form.title} onChange={set("title")} />
            </div>
            <div className="emp-field">
              <label className="emp-label">Job Description *</label>
              <textarea className="emp-textarea" rows={6} placeholder="Describe the role, responsibilities, and what makes your salon a great place to work…" value={form.description} onChange={set("description")} style={{ minHeight: 140 }} />
              <div className="pj-char-count">{form.description.length} characters</div>
            </div>
            <div className="emp-grid2">
              <div className="emp-field">
                <label className="emp-label">Specialisation *</label>
                <select className="emp-select" value={form.spec} onChange={set("spec")}>
                  <option value="">Select specialisation</option>
                  {globalSpecialisations.map(s => {
                    // FIX 3: Dynamic handling for String array vs DTO Object array 
                    const id = s.id !== undefined ? s.id : s;
                    const name = s.name !== undefined ? s.name : s;
                    return <option key={id} value={id}>{name}</option>;
                  })}
                </select>
              </div>
              <div className="emp-field">
                <label className="emp-label">Job Type</label>
                <select className="emp-select" value={form.type} onChange={set("type")}>
                  {["Full-time", "Part-time", "Freelance", "Contract"].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Location & Salary Card */}
          <div className="pj-card">
            <div className="pj-card-title">Location & <em>salary</em></div>
            <div className="emp-field">
              <label className="emp-label">City / Location *</label>
              <select className="emp-select" value={form.city} onChange={set("city")}>
                <option value="">Select your Location...</option>
                {countiesList.map(county => (
                  <option key={county.id} value={county.name}>{county.name}</option>
                ))}
              </select>
            </div>
            <div className="emp-grid2">
              <div className="emp-field">
                <label className="emp-label">Min Salary</label>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <select className="emp-select" style={{ width: 80 }} value={form.salaryCurrency} onChange={set("salaryCurrency")}>
                    <option>KSh</option><option>USD</option><option>UGX</option>
                  </select>
                  <input className="emp-input" type="number" placeholder="e.g. 40000" value={form.salaryMin} onChange={set("salaryMin")} />
                </div>
              </div>
              <div className="emp-field">
                <label className="emp-label">Max Salary</label>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <select className="emp-select" style={{ width: 80 }} value={form.salaryPer} onChange={set("salaryPer")}>
                    <option value="month">/ mo</option><option value="day">/ day</option>
                  </select>
                  <input className="emp-input" type="number" placeholder="e.g. 65000" value={form.salaryMax} onChange={set("salaryMax")} />
                </div>
              </div>
            </div>
          </div>

          {/* Requirements & Skills Card */}
          <div className="pj-card">
            <div className="pj-card-title">Requirements & <em>skills</em></div>
            <div className="emp-grid2">
              <div className="emp-field">
                <label className="emp-label">Experience Required</label>
                <select className="emp-select" value={form.experience} onChange={set("experience")}>
                  <option value="">Any level</option>
                  {["Entry level (0–1 yr)", "Junior (1–3 yrs)", "Mid-level (3–5 yrs)", "Senior (5+ yrs)", "Management"].map(e => <option key={e}>{e}</option>)}
                </select>
              </div>
              <div className="emp-field">
                <label className="emp-label">Application Deadline</label>
                <input className="emp-input" type="date" value={form.deadline} onChange={set("deadline")} />
              </div>
            </div>

            {/* Auto-suggest Skills Section */}
            <div className="emp-field" style={{ marginBottom: 0 }}>
              <label className="emp-label">Required Skills</label>
              
              <div className="pj-skills-wrap" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: form.skills.length > 0 ? '0.75rem' : '0' }}>
                {form.skills.map(s => (
                  <span key={s} className="pj-skill-tag">
                    {s}
                    <button type="button" className="pj-skill-remove" onClick={() => removeSkill(s)}>✕</button>
                  </span>
                ))}
              </div>

              <div ref={dropdownRef} style={{ position: 'relative' }}>
                <div className="pj-skill-input-row" style={{ display: 'flex', gap: '0.5rem' }}>
                  <input 
                    className="emp-input" 
                    placeholder="Type to search and add skills (e.g. Balayage)..." 
                    value={skillInput} 
                    onChange={e => {
                      setSkillInput(e.target.value);
                      setShowSkillDropdown(true);
                    }}
                    onFocus={() => setShowSkillDropdown(true)}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (skillInput.trim()) {
                          const topSelection = filteredSkillsPool[0];
                          const dynamicName = topSelection && (typeof topSelection === 'string' ? topSelection : topSelection.name);
                          handleSelectSkill(dynamicName || skillInput.trim());
                        }
                      }
                    }} 
                  />
                  <button 
                    type="button"
                    className="pj-skill-add-btn" 
                    onClick={() => {
                      if (skillInput.trim()) {
                        const topSelection = filteredSkillsPool[0];
                        const dynamicName = topSelection && (typeof topSelection === 'string' ? topSelection : topSelection.name);
                        handleSelectSkill(dynamicName || skillInput.trim());
                      }
                    }}
                  >
                    + Add
                  </button>
                </div>

                {showSkillDropdown && skillInput.trim().length > 0 && (
                  <div style={{ 
                    position: 'absolute', top: '100%', left: 0, right: 0, 
                    background: '#fff', border: '1px solid #e0e0e0', borderRadius: '4px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 10, maxHeight: '200px', overflowY: 'auto'
                  }}>
                    {filteredSkillsPool.length > 0 ? (
                      filteredSkillsPool.map((pooledSkill, idx) => {
                        const itemDisplay = typeof pooledSkill === 'string' ? pooledSkill : pooledSkill.name;
                        return (
                          <div 
                            key={idx}
                            onClick={() => handleSelectSkill(itemDisplay)}
                            style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #f9f9f9', color: '#333' }}
                            onMouseEnter={(e) => e.target.style.background = '#fafafa'}
                            onMouseLeave={(e) => e.target.style.background = '#fff'}
                          >
                            {itemDisplay}
                          </div>
                        );
                      })
                    ) : (
                      <div style={{ padding: '10px 14px', color: '#999', fontSize: '0.85rem' }}>
                        Press enter or click "+ Add" to save custom skill "{skillInput}"
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="pj-form-actions">
            <button className="emp-btn-ghost" onClick={() => navigate("/employer/listings")}>Cancel</button>
            <button 
              className="emp-btn-primary" 
              onClick={handleSubmit}
              disabled={loading}
              style={{ opacity: (form.title && form.description && form.spec && form.city && !loading) ? 1 : 0.55 }}
            >
              {loading ? "Publishing..." : "Publish Job Listing"}
            </button>
          </div>
        </div>

        {/* Live Sidebar Preview Display */}
        <div>
          <div className="pj-preview-card">
            <div className="pj-preview-title">Listing <em>preview</em></div>
            <div className="pj-preview-job-title">{form.title || "Job title"}</div>
            {/* <div className="pj-preview-company">Luxe Hair Studio</div> */}
            <div className="pj-preview-tags">
              {form.city && <span className="emp-tag emp-tag-loc">📍 {form.city}</span>}
              {form.type && <span className="emp-tag emp-tag-type">{form.type}</span>}
              {previewSpecName && <span className="emp-tag emp-tag-match">{previewSpecName}</span>}
            </div>
            <div className="pj-preview-desc">{form.description || "Your job description will appear here…"}</div>
            <div className="pj-preview-divider" />
            {[["Salary", salaryPreview], ["Experience", form.experience || "—"], ["Deadline", form.deadline || "—"]].map(([k, v]) => (
              <div key={k} className="pj-preview-row"><span className="pj-preview-key">{k}</span><span className="pj-preview-val">{v}</span></div>
            ))}
          </div>

          <div className="pj-tips-card">
            <div className="pj-tips-title">Tips for a great listing</div>
            <ul className="pj-tips-list">
              <li>Be specific about the skills and experience you need</li>
              <li>Include salary — listings with salary get 3× more applicants</li>
              <li>Describe your salon's culture and what makes it a great place to work</li>
              <li>Keep the title clear and searchable</li>
            </ul>
          </div>
        </div>
      </div>
    </EmpLayout>
  );
}